import { z } from 'zod';
import { PLATFORM_LIST } from '@/constants/platforms';
import type { Platform } from '@/types';

export function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Client-side platform detection; the backend re-validates authoritatively. */
export function detectPlatformFromUrl(value: string): Platform | null {
  if (!isValidHttpUrl(value)) return null;
  for (const platform of PLATFORM_LIST) {
    if (platform.patterns.some((pattern) => pattern.test(value))) return platform.id;
  }
  return null;
}

export const urlFormSchema = z.object({
  url: z
    .string()
    .trim()
    .min(1, 'Paste a media URL to get started')
    .url('That doesn’t look like a valid URL')
    .max(2048, 'URL is too long')
    .refine((value) => detectPlatformFromUrl(value) !== null, {
      message: 'Unsupported link — paste a public Instagram Reel, post, IGTV or video URL',
    }),
});

export type UrlFormValues = z.infer<typeof urlFormSchema>;

/** Pull the first http(s) URL out of dropped/pasted text. */
export function extractUrlFromText(text: string): string | null {
  const match = text.match(/https?:\/\/\S+/);
  return match ? match[0] : null;
}
